import BaseService from "react-lib/apis/services/BaseService";

export const PUR_REQUEST_TIMEOUT_MS = 30000;

export interface PurClientConfig {
  apiBaseUrl: string;
  apiToken?: string;
  divisionId?: number | string | null;
  pathPrefix?: string;
}

// Untrusted HTTP responses enter here, not directly into screen models.
export const readObject = (value: unknown): Record<string, unknown> => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Invalid PUR object response");
  }
  return value as Record<string, unknown>;
};

export const readText = (value: unknown): string =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";

export const readId = (value: unknown): number | null => {
  if (typeof value !== "number" && (typeof value !== "string" || !/^\d+$/.test(value))) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

export const requireId = (value: unknown): number => {
  const id = readId(value);
  if (id === null) throw new Error("PUR record has no valid ID");
  return id;
};

// Keep server precision, including zero. Missing V2 fields are not defaults.
export const readDecimal = (value: unknown): string | null => {
  const text = readText(value);
  return /^-?\d+(?:\.\d+)?$/.test(text) && Number.isFinite(Number(text)) ? text : null;
};

export const readBoolean = (value: unknown): boolean | null => typeof value === "boolean" ? value : null;

export const readPage = <T>(value: unknown, normalize: (row: unknown) => T): { count: number; records: T[] } => {
  const page = readObject(value);
  // total/items is the contract. count/results remains a compatibility bridge.
  const rows = Array.isArray(page.items) ? page.items : page.results;
  if (!Array.isArray(rows)) throw new Error("Invalid PUR list response");
  const rawCount = readDecimal(page.total ?? page.count);
  const count = rawCount === null ? NaN : Number(rawCount);
  if (!Number.isSafeInteger(count) || count < 0) throw new Error("PUR list has no valid count");
  return { count, records: rows.map(normalize) };
};

export const pageParams = (limit: number, offset: number): URLSearchParams => {
  if (!Number.isSafeInteger(limit) || limit <= 0 || !Number.isSafeInteger(offset) || offset < 0) {
    throw new Error("Invalid PUR pagination query");
  }
  return new URLSearchParams({ limit: String(limit), offset: String(offset) });
};

export const purRequestOptions = (config: PurClientConfig, path: string): { url: string; headers: Record<string, string> } => {
  const host = config.apiBaseUrl.replace(/\/$/, "");
  const prefix = (config.pathPrefix || "/apis/PUR").replace(/^\/?/, "/").replace(/\/$/, "");
  const headers: Record<string, string> = {};
  if (config.apiToken) headers.Authorization = `Token ${config.apiToken}`;
  if (config.divisionId !== null && config.divisionId !== undefined) headers.DIVISION = String(config.divisionId);
  return { url: `${host}${prefix}/${path}`, headers };
};

export const getPur = async (config: PurClientConfig, path: string): Promise<unknown> => {
  // Query stays in the URL: BaseService otherwise adds page_number pagination.
  const { url, headers } = purRequestOptions(config, path);
  const response = await new BaseService().client.get(url, { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
  return response.data;
};
